import PropTypes from "prop-types";
import React, { Component, useRef, useCallback } from "react";
import WebConfig from "middlewares/WebConfig";
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer, Polygon, Circle, Rectangle, DrawingManager, InfoBox } from "@react-google-maps/api";
import { fontColor } from "components/Shared/systemColors";
import { darkMapTheme } from "./MapStyles.ts";
import { withMuellerTheme } from "./MuellerThemeProvider.tsx";
import { surveyTypeGroupCodes } from "typeConstants/surveyTypeGroupCodes.ts";

/* global google */

const libraries = ["geometry", "drawing", "places"];

export const StandardPinSize = { width: 15, height: 25 };

const CreatePinObject = (icon) => {
    if (!icon) return null;
    return {
        url: icon.url,
        ...((icon.width && icon.height) && { scaledSize: new google.maps.Size(icon.width, icon.height) }),
        ...((icon.anchorX && icon.anchorY) && { anchor: new google.maps.Point(icon.anchorX, icon.anchorY) })
    };
};

const googleDrawFinished = (pins, callback, frGeoCoverageMode, saveFRServiceBoundary) => {
    return (event) => {
        let polyCoords = [];
        let center;
        const overlay = event.overlay || event;
        const eventType = event.type || (overlay.getRadius ? google.maps.drawing.OverlayType.CIRCLE : 
                          overlay.getPath ? google.maps.drawing.OverlayType.POLYGON : 
                          google.maps.drawing.OverlayType.RECTANGLE);
        
        if (callback) callback(
            pins.filter((pin) => {
                let latLng = new google.maps.LatLng(pin.latitude, pin.longitude);
                switch (eventType) {
                    case google.maps.drawing.OverlayType.CIRCLE:
                        center = overlay.getCenter().toString();
                        return google.maps.geometry.spherical.computeDistanceBetween(latLng, overlay.getCenter()) <= overlay.getRadius();
                    case google.maps.drawing.OverlayType.POLYGON:
                        // Back end expects data in longtitude/latitude order
                        if (frGeoCoverageMode) polyCoords.push(overlay.getPath().getArray().map(p => [p.lng(), p.lat()]));
                        return google.maps.geometry.poly.containsLocation(latLng, overlay);
                    case google.maps.drawing.OverlayType.RECTANGLE:
                        return overlay.getBounds().contains(latLng);
                }
            })
        );
        if (frGeoCoverageMode) {
            let payload = {
                "type": eventType[0].toUpperCase() + eventType.slice(1),
                "mode": "draw",
            };
            switch (payload.type) { 
                case "Circle":
                    center = center.replace("(", "").replace(")", "").split(",").map(c => Number(c));
                    payload["radius"] = overlay.getRadius();
                    payload["center"] = { lat: center[0], lng: center[1] };
                    break;
                case "Polygon":
                    payload["coordinates"] = [polyCoords[0]];
                    break;
                case "Rectangle":
                    payload["bounds"] = overlay.getBounds();
                    break;
                case "default":
                    break;
            }
            let boundary = {
                payload, 
                "clearMapCallback": () => overlay.setMap(null),
            };
            saveFRServiceBoundary(boundary);
        } else overlay.setMap(null);
    };
};

const MapConstInner = (props) => {
    const mapRef = useRef(null);
    const drawingManagerRef = useRef(null);
    let mapTheme = { styles: [] };
    if (props.themeContext.currentTheme.isDark) mapTheme = darkMapTheme;

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        if (props.mapRefFunc) {
            props.mapRefFunc({
                context: {
                    __SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED: map
                }
            });
        }
    }, [props.mapRefFunc]);

    const onDrawingManagerLoad = useCallback((dm) => {
        drawingManagerRef.current = dm;
        if (props.drawingManagerRefFunc) {
            props.drawingManagerRefFunc({
                state: {
                    __SECRET_DRAWING_MANAGER_DO_NOT_USE_OR_YOU_WILL_BE_FIRED: dm
                }
            });
        }
    }, [props.drawingManagerRefFunc]);

    const handleOverlayComplete = useCallback((e) => {
        googleDrawFinished(props.pins, props.onSelectionCompleted, props.isFRGeoCoverageMode, props.saveFRServiceBoundary)(e);
    }, [props.pins, props.onSelectionCompleted, props.isFRGeoCoverageMode, props.saveFRServiceBoundary]);

    return (
        <GoogleMap
            onLoad={onMapLoad}
            zoom={props.zoom}
            center={props.center}
            mapTypeId={props.mapType}
            onClick={e => props.onMapClick(e)}
            onRightClick={e => props.onMapRightClick(e)}
            options={{
                streetViewControl: props.streetView,
                ...mapTheme,
            }}
            mapContainerClassName={props.mapClass}
        >
            {props.directions && <DirectionsRenderer directions={props.directions} options={{ markerOptions: { visible: false } }} />}
            {!!props.pins && props.pins.map(pin => (
                <Marker
                    key={pin.id}
                    position={{ lat: pin.latitude, lng: pin.longitude }}
                    icon={CreatePinObject(pin.icon)}
                    {...props.onClick && { onClick: () => props.onClick(pin) }}
                    {...(props.onMouseOver && props.onMouseOut) && { onMouseOver: () => props.onMouseOver(pin), onMouseOut: () => props.onMouseOut(pin) }}
                />))}
            {props.enableSelection && google.maps.drawing &&
                <DrawingManager
                    onLoad={onDrawingManagerLoad}
                    onOverlayComplete={handleOverlayComplete}
                    options={{
                        drawingControl: true,
                        drawingControlOptions: {
                            position: google.maps.ControlPosition.TOP_CENTER,
                            drawingModes: [
                                google.maps.drawing.OverlayType.CIRCLE,
                                google.maps.drawing.OverlayType.POLYGON,
                                google.maps.drawing.OverlayType.RECTANGLE,
                            ],
                        },
                    }}
                />}
            {props.polygons && props.polygons.map(p => (
                <Polygon
                    key={p.id}
                    {...p.path && { path: p.path }} // path just allows for one array points
                    {...p.paths && { paths: p.paths }} // paths allows for multiple paths, which also allows for holes in the polygon if the winding order is reversed
                    options={{
                        fillColor: p.fillColor,
                        fillOpacity: p.fillOpacity ?? 0.2,
                        strokeColor: p.strokeColor,
                        strokeOpacity: p.strokeOpacity ?? 1,
                        strokeWeight: p.strokeWeight ?? 1,
                        zIndex: p.zIndex,
                    }}
                    onClick={p.onClick}
                    onMouseOver={() => p.onMouseOver(p)}
                    onMouseOut={() => p.onMouseOut(p)}
                />
                ))}
            {props.showInfoBox &&
                <>
                    <InfoBox
                        options={props.infoBoxProps.infoBoxOptions}
                        position={new google.maps.LatLng(props.infoBoxProps.infoBoxCoords.lat, props.infoBoxProps.infoBoxCoords.lng)}>
                        <div style={{ backgroundColor: "lightgrey", opacity: 0.85, padding: 10, borderRadius: "20px" }}>
                            <div style={{ fontSize: 12, fontColor: `black`, fontWeight: "bold", width: "fit-contents" }}>
                                {props.infoBoxProps.infoBoxChildren}
                            </div>
                        </div>
                    </InfoBox>
                </>} 
            {(props.serviceBoundary && props.canViewFRServiceRegion) && parseServiceRegion(props.serviceBoundary, props.inspectorId, props.inspectorName, props.onMouseOver, props.onMouseOut)}
            {(!props.inspectorNumber && props.canViewFRServiceRegion) && props.selectedServiceRegions?.map(si => parseServiceRegion(si.boundary, si.inspectorNumber, si.inspectorName, props.onMouseOver, props.onMouseOut))}
        </GoogleMap>
    );
};

MapConstInner.propTypes = {
    themeContext: PropTypes.object,
    mapRefFunc: PropTypes.func,
    drawingManagerRefFunc: PropTypes.func,
    pins: PropTypes.array,
    onSelectionCompleted: PropTypes.func,
    isFRGeoCoverageMode: PropTypes.bool,
    saveFRServiceBoundary: PropTypes.func,
    zoom: PropTypes.number,
    center: PropTypes.object,
    mapType: PropTypes.string,
    onMapClick: PropTypes.func,
    onMapRightClick: PropTypes.func,
    streetView: PropTypes.bool,
    mapClass: PropTypes.string,
    directions: PropTypes.object,
    onClick: PropTypes.func,
    onMouseOver: PropTypes.func,
    onMouseOut: PropTypes.func,
    enableSelection: PropTypes.bool,
    polygons: PropTypes.array,
    showInfoBox: PropTypes.bool,
    infoBoxProps: PropTypes.object,
    serviceBoundary: PropTypes.string,
    canViewFRServiceRegion: PropTypes.bool,
    inspectorId: PropTypes.number,
    inspectorName: PropTypes.string,
    inspectorNumber: PropTypes.number,
    selectedServiceRegions: PropTypes.array,
};

const MapConst = withMuellerTheme(MapConstInner);

export const parseServiceRegion = (boundary, inspectorId, inspectorName, mouseOver, mouseOut) => {
    // Data from the backend is in longitude/latitude format
    const coordPairToLatLng = ([lat, lng]) => ({lat: lng, lng: lat});
    boundary = JSON.parse(boundary);
    let shape = boundary.type;
    let center = shape === "Circle" ? boundary.center : null;
    let radius = shape === "Circle" ? boundary.radius : null;
    let bounds = shape === "Rectangle" ? boundary.bounds : null;
    if (shape === "Polygon") boundary = boundary.coordinates[0].map(coordPairToLatLng); 
    if (shape === "MultiPolygon") boundary = boundary.coordinates.map(c => c.reduce((acc, arr) => acc.concat(arr.map(coordPairToLatLng)), []));
    let color = fontColor.disabled;
    let ret = {
      serviceBoundary: {
        id: inspectorId,
        shape: shape,
        paths: boundary,
        center: center,
        radius: radius,
        bounds: bounds,
        mouseOverObj: {
            type: "region",
            value: `Service region for ${inspectorName}.`
        },
        options: {
          fillColor: color,
          strokeColor: fontColor.disabled,
          fillOpacity: 0.5,
          strokeOpacity: 1,
          strokeWeight: 1,
          zIndex: 10,
        },
        onMouseOver: mouseOver,
        onMouseOut: mouseOut,
      }
    };
    return displayFieldRepServiceRegion(ret);
  };

function displayFieldRepServiceRegion(sb) {
    switch (sb.serviceBoundary.shape) {
        case "Polygon":
        case "MultiPolygon":
            return ( 
                <Polygon 
                    key={sb.serviceBoundary.id}
                    {...sb.serviceBoundary.paths && { paths: sb.serviceBoundary.paths }}
                    {...sb.serviceBoundary.path && { path: sb.serviceBoundary.path }}
                    options={sb.serviceBoundary.options} 
                    onMouseOver={() => sb.serviceBoundary.onMouseOver(sb.serviceBoundary.mouseOverObj)}
                    onMouseOut={() => sb.serviceBoundary.onMouseOut(sb.serviceBoundary.mouseOverObj)}
                />
            );
        case "Circle":
            return (
                <Circle 
                    key={sb.serviceBoundary.id}
                    radius={sb.serviceBoundary.radius}
                    center={sb.serviceBoundary.center}
                    visible={true}
                    options={sb.serviceBoundary.options}
                    onMouseOver={() => sb.serviceBoundary.onMouseOver(sb.serviceBoundary.mouseOverObj)}
                    onMouseOut={() => sb.serviceBoundary.onMouseOut(sb.serviceBoundary.mouseOverObj)}
                />
            );
        case "Rectangle":
            return ( 
                <Rectangle 
                    key={sb.serviceBoundary.id}
                    bounds={sb.serviceBoundary.bounds} 
                    options={sb.serviceBoundary.options}
                    onMouseOver={() => sb.serviceBoundary.onMouseOver(sb.serviceBoundary.mouseOverObj)}
                    onMouseOut={() => sb.serviceBoundary.onMouseOut(sb.serviceBoundary.mouseOverObj)}
                /> 
            );
        default:
            null;
    }
}

export function getCasePinURL(color, isSelected, isSurge, isFiltered) {
    return `${WebConfig.mapPinRoot}pin_${color}${isSelected ? "_selected" : ""}${isSurge && !isSelected && !isFiltered ? "_surge" : ""}${isFiltered && !isSelected ? "_quicklist" : ""}.png`;
}

export function getInspectorPinURL(isSelected, isLiveLocation) {
    return `${WebConfig.mapPinRoot}pinspector${isLiveLocation ? "_live" : ""}${isSelected ? "_selected" : ""}.png`;
}

export const getCasePinIconURL = (surveyTypeGroupCode, isSelected, isSurge, isFiltered) => {
    // All of these lender survey type groups use the same icon, not sure of a better way to handle yet
    let groupCode = surveyTypeGroupCode;
    switch(surveyTypeGroupCode) {
        case surveyTypeGroupCodes._1004_DESKTOP:
        case surveyTypeGroupCodes._1004_HYBRID:
        case surveyTypeGroupCodes._1004D:
        case surveyTypeGroupCodes.LENDER:
        case surveyTypeGroupCodes.LENDER_COMMERCIAL:
        case surveyTypeGroupCodes.MVALUE:
        case surveyTypeGroupCodes.MVALUE_I:
        case surveyTypeGroupCodes.MVALUE_I_SUPP:
        case surveyTypeGroupCodes.MVALUE_SUPP:
            groupCode = "LENDER";
            break;
    }
    return `${WebConfig.mapIconPinRoot}${groupCode}${isSelected ? "_selected" : ""}${isSurge ? "_surge" : ""}${isFiltered ? "_filtered" : ""}.png`;
};

class Map extends Component {
    constructor(props) {
        super(props);

        this.state = {
            routing: null,
            directions: null,
            lastUpdate: 0,
        };

        this.handleRouting = this.handleRouting.bind(this);
        this.keyDown = this.keyDown.bind(this);
        this.keyUp = this.keyUp.bind(this);
        this.drawingManagerRefFunc = this.drawingManagerRefFunc.bind(this);
        this.mapRefFunc = this.mapRefFunc.bind(this);

        this.drawingManager = null;
        this.lastDrawingTool = null;
    }

    componentDidMount() {
        if (this.props.enableSelection) {
            document.getElementById("muellerMapControl").addEventListener("keydown", this.keyDown, true);
            document.getElementById("muellerMapControl").addEventListener("keyup", this.keyUp, true);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!this.props.manualUpdate) return true;
        return this.props.lastUpdate !== nextProps.lastUpdate || this.state.lastUpdate !== nextState.lastUpdate;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.handleRouting(nextProps.routing);
    }

    handleRouting(nextRouting) {

        let oldRouting = this.state.routing;

        let routingChanged = false;

        //routing turned off
        if (oldRouting && !nextRouting) {
            this.setState({ routing: null, directions: null, lastUpdate: Date.now() });
        }

        if (!oldRouting && nextRouting) {
            routingChanged = true;
        } else if (oldRouting && nextRouting) {
            //check if origin, destination, or waypoints have been changed
            if (!this.positionEqual(oldRouting.origin, nextRouting.origin) ||
                !this.positionEqual(oldRouting.destination, nextRouting.destination) ||
                ((oldRouting.waypoints ? oldRouting.waypoints.length : 0) != (nextRouting.waypoints ? nextRouting.waypoints.length : 0)) ||
                !oldRouting.waypoints.every((value, index) => this.positionEqual(value, nextRouting.waypoints[index]))) {
                routingChanged = true;
            }
        }

        if (routingChanged) {
            this.setState({ routing: nextRouting, directions: null, lastUpdate: Date.now() });

            const DirectionsService = new google.maps.DirectionsService();

            DirectionsService.route({
                origin: new google.maps.LatLng(nextRouting.origin.latitude, nextRouting.origin.longitude),
                destination: new google.maps.LatLng(nextRouting.destination.latitude, nextRouting.destination.longitude),
                ...((nextRouting.waypoints && nextRouting.waypoints.length) && { waypoints: nextRouting.waypoints.map(wp => { return { location: new google.maps.LatLng(wp.latitude, wp.longitude), stopover: true }; }) }),
                optimizeWaypoints: true,
                travelMode: google.maps.TravelMode.DRIVING
            }, (result, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                    this.setState({ directions: result, lastUpdate: Date.now() });
                    if (this.props.onRoutingFinished) this.props.onRoutingFinished({
                        success: true,
                        distance: result.routes[0].legs.reduce((total, l) => total + l.distance.value, 0),
                        duration: result.routes[0].legs.reduce((total, l) => total + l.duration.value, 0)
                    });
                } else {
                    alert(result.status);
                    if (this.props.onRoutingFinished) this.props.onRoutingFinished({ success: false });
                }
            });
        }
    }

    keyDown(event) {
        if (event.repeat || !this.drawingManager) return;

        if (event.key === "1") this.drawingManager.setDrawingMode(null);
        if (event.key === "2") this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
        if (event.key === "3") this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
        if (event.key === "4") this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE);

        if (event.key === " " || event.key === "Spacebar") {
            this.lastDrawingTool = this.drawingManager.getDrawingMode();
            this.drawingManager.setDrawingMode(null);
        }
    }

    keyUp(event) {
        if (!this.drawingManager) return;

        if (event.key === " " || event.key === "Spacebar") {
            this.drawingManager.setDrawingMode(this.lastDrawingTool);
            this.props.onMouseOut(); // must mouseout otherwise hovers stay
        }
    }

    drawingManagerRefFunc(node) {
        if (node) {
            this.drawingManager = node.state.__SECRET_DRAWING_MANAGER_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
        } else {
            this.drawingManager = null;
        }
    }

    mapRefFunc(node) {
        if (!this.props.getMapController) return;
        if (node) {
            let map = node.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
            this.props.getMapController({
                setZoom: (level) => map.setZoom(level),
                setCenter: (latitude, longitude) => map.setCenter({ lat: latitude, lng: longitude })
            });
        } else {
            this.props.getMapController(null);
        }
    }

    positionEqual(o1, o2) {
        return o1.latitude === o2.latitude && o1.longitude === o2.longitude;
    }

    render() {
        return (
            <div id="muellerMapControl" className={this.props.containerClass}>
                <MapConst
                    mapRefFunc={this.mapRefFunc}
                    mapType={this.props.mapType}
                    zoom={this.props.zoom}
                    center={this.props.center}
                    drawingManagerRefFunc={this.drawingManagerRefFunc}
                    pins={this.props.pins}
                    enableSelection={this.props.enableSelection}
                    onSelectionCompleted={this.props.onSelectionCompleted}
                    mapClass={this.props.mapClass}
                    onClick={this.props.onClick}
                    onMapClick={this.props.onMapClick}
                    onMapRightClick={this.props.onMapRightClick}
                    onMouseOver={this.props.onMouseOver}
                    onMouseOut={this.props.onMouseOut}
                    directions={this.state.directions}
                    polygons={this.props.polygons}
                    showInfoBox={this.props.showInfoBox}
                    infoBoxProps={this.props.infoBoxProps}
                    streetView={this.props.streetView}
                    isFRGeoCoverageMode={this.props.isFRGeoCoverageMode}
                    saveFRServiceBoundary={this.props.saveFRServiceBoundary}
                    serviceBoundary={this.props.serviceBoundary}
                    inspectorNumber={this.props.inspectorNumber}
                    inspectorName={this.props.inspectorName}
                    canViewFRServiceRegion={this.props.canViewFRServiceRegion}
                    selectedServiceRegions={this.props.selectedServiceRegions}
                />
            </div>
        );
    }
}

Map.defaultProps = {
    zoom: 5,
    center: { lat: 40, lng: -95 },
    mapType: "roadmap",
    onMapClick: () => { },
    onMapRightClick: () => { },
    streetView: true,
};

Map.propTypes = {
    onSelectionCompleted: PropTypes.func,
    onClick: PropTypes.func, //this will be called with the pin that was clicked
    onMapClick: PropTypes.func, //this will return the map data for the location clicked (i.e. lat/lng)
    onMapRightClick: PropTypes.func, //this will return the map data for the location clicked (i.e. lat/lng)
    onMouseOver: PropTypes.func, //will be called with the pin that was moused over
    onMouseOut: PropTypes.func, //will be called when the mouse leaves with the pin that the mouse was over
    //this is a weird one, if supplied, this function will be called with a reference to an object that gives you control of the map
    //i couldnt just use state to control the map because the user can also control the map by dragging it and that doesnt always change state
    //also changing map position by using state is very slow because it causes all of the pins to re render on the map
    getMapController: PropTypes.func,
    onRoutingFinished: PropTypes.func,
    /*
    {
        id: number, // used for key on react object
        latitude: number,
        longitude: number,
        icon: {
            url: string, // look at getCasePinURL and getInspectorPinURL
            width: number, // just use StandardPinSize.width, code should get changed to default to this
            height: number, // just use StandardPinSize.height, code should get changed to default to this
            anchorX, // the code does something with these but it seems to never be used
            anchorY, // the code does something with these but it seems to never be used
        },
    }
    */
    pins: PropTypes.array,
    containerClass: PropTypes.string,
    mapClass: PropTypes.string,
    loadingClass: PropTypes.string,
    enableSelection: PropTypes.bool,
    manualUpdate: PropTypes.bool,
    lastUpdate: PropTypes.number,
    routing: PropTypes.object,
    /*
    {
        id: string, // used for the key on the react object
        type: ???, // not really sure about this
        path: point array,
        paths: array of point arrays,
        zIndex: take a guess what this does...,

        fillColor: string,
        fillOpacity: string,
        stokeOpacity: number - default 0.2,
        strokeWeight: number - default 1,
        strokeColor: string,

        onMouseOver: func,
        onMouseOut: func,
        onClick: func,
    }
    */
    polygons: PropTypes.array,
    mapType: PropTypes.string, // map type id (i.e. satellite)
    showInfoBox: PropTypes.bool, // toggle display of an InfoBox on the map
    infoBoxProps: PropTypes.object, // properties and children for the InfoBox component
    zoom: PropTypes.number, // Allows specification of a defaultZoom 
    center: PropTypes.object, // Allows specification of the defaultCenter
    streetView: PropTypes.bool, // true by default, removes Pegman from display (only applicable to Appraiser Coverage Map)
    isFRGeoCoverageMode: PropTypes.bool,
    saveFRServiceBoundary: PropTypes.func,
    serviceBoundary: PropTypes.string,
    inspectorNumber: PropTypes.number,
    canViewFRServiceRegion: PropTypes.bool,
    selectedServiceRegions: PropTypes.array,
    inspectorName: PropTypes.string,
    inspectorId: PropTypes.number,
    themeContext: PropTypes.object,
    mapRefFunc: PropTypes.func,
    drawingManagerRefFunc: PropTypes.func,
    directions: PropTypes.object,
};

// Wrapper component to handle Google Maps script loading
const MapWithLoader = (props) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: WebConfig.googleMapsApiKey,
        libraries,
    });

    if (loadError) {
        return <div>Error loading maps</div>;
    }

    if (!isLoaded) {
        return <div className={props.loadingClass}>Loading...</div>;
    }

    return <Map {...props} />;
};

MapWithLoader.propTypes = {
    ...Map.propTypes,
    loadingClass: PropTypes.string,
};
MapWithLoader.defaultProps = Map.defaultProps;

export default MapWithLoader;
